import React, { useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { ButtonBase as MuiButtonBase } from '@mui/material';
import { diffMin, getHour, getMin } from '../../../Utils/dateUtils';
import {
  IBooking,
  IBookingIcon,
  bookingTimeIntervalCaption,
  timeToPos,
} from '../Utils/EDCalendarViewUtils';
import EDTypography from '../../Typography/EDTypography';
import EDBox from '../../EDBox/EDBox';
import EDIcon from '../../Icon/EDIcon';
import EDTooltip from '../../Tooltip/EDTooltip';

interface IProps {
  booking: IBooking;
  startTime: Date;
  endTime: Date;
  viewportWidth: number;
  viewportHeight: number;
  columnWidth: number;
  selected?: boolean;
  onBookingClick?: (ref: EventTarget, booking: IBooking) => void;
  isDragging?: boolean;
  testId?: string;
}

interface IBookingBoxProps {
  isDragging?: boolean;
  prelBokad?: boolean;
}

export const EDCalendarViewTimeViewBooking: React.FC<IProps> = props => {
  const { cssVariables, showShadow } = useMemo(() => {
    const startTime =
      props.booking.startTime > props.startTime
        ? props.booking.startTime
        : props.startTime;
    const endTime =
      props.booking.endTime < props.endTime
        ? props.booking.endTime
        : props.endTime;
    const aktStartTime =
      props.booking.aktStartTime &&
      diffMin(startTime, props.booking.aktStartTime) > 0
        ? props.booking.aktStartTime
        : startTime;
    const aktEndTime =
      props.booking.aktEndTime && diffMin(props.booking.aktEndTime, endTime) > 0
        ? props.booking.aktEndTime
        : endTime;
    const posFr = timeToPos(
      getHour(startTime),
      getMin(startTime),
      props.viewportHeight
    );
    const posTo = timeToPos(
      getHour(endTime),
      getMin(endTime),
      props.viewportHeight
    );
    const aktPosFr = timeToPos(
      getHour(aktStartTime),
      getMin(aktStartTime),
      props.viewportHeight
    );
    const aktPosTo = timeToPos(
      getHour(aktEndTime),
      getMin(aktEndTime),
      props.viewportHeight
    );
    const grpIdx = props.booking.columnIndex ?? 0;
    const grpCnt = props.booking.columnCount || 1;
    const bookingLeft = (100 * grpIdx) / grpCnt;
    const bookingWidth = 100 / grpCnt;
    const showShadow = posFr < aktPosFr || posTo > aktPosTo;

    const cssVariables = {
      '--bookingLeft': `${bookingLeft}%`,
      '--bookingTop': `${aktPosFr}px`,
      '--bookingWidth': `${bookingWidth}%`,
      '--bookingHeight': `${aktPosTo - aktPosFr - 1}px`,
      '--bookingBackground': props.booking.backgroundLight,
      '--bookingBackgroundHover': props.booking.background,
      '--bookingBorderColor': props.booking.color,
      '--bookingShadowLeft': `${bookingLeft}%`,
      '--bookingShadowTop': `${posFr}px`,
      '--bookingShadowWidth': `${bookingWidth}%`,
      '--bookingShadowHeight': `${posTo - posFr - 1}px`,
      '--bookingShadowBackground': props.booking.shadowBackground ?? 'white',
    } as React.CSSProperties;

    return {
      cssVariables,
      showShadow,
    };
  }, [
    props.startTime,
    props.endTime,
    props.viewportHeight,
    props.booking.startTime,
    props.booking.endTime,
    props.booking.aktEndTime,
    props.booking.aktStartTime,
    props.booking.columnIndex,
    props.booking.columnCount,
    props.booking.color,
    props.booking.background,
    props.booking.backgroundLight,
    props.booking.shadowBackground,
  ]);

  const onBookingClick = props.onBookingClick;

  const handleMouseDownAndUp = useCallback((event: React.MouseEvent) => {
    if (!event.ctrlKey && !event.shiftKey) {
      event.stopPropagation();
    }
  }, []);

  const handleBookingClick = useCallback(
    (event: React.MouseEvent) => {
      if (!event.ctrlKey && !event.shiftKey && onBookingClick) {
        onBookingClick(event.target, props.booking);
      }
      event.stopPropagation();
    },
    [onBookingClick, props.booking]
  );

  return (
    <>
      {showShadow && (
        <BookingShadowBox
          onMouseDown={handleMouseDownAndUp}
          onMouseUp={handleMouseDownAndUp}
          isDragging={props.isDragging}
          style={cssVariables}
        />
      )}
      <BookingBox
        style={cssVariables}
        onMouseDown={handleMouseDownAndUp}
        onMouseUp={handleMouseDownAndUp}
        isDragging={props.isDragging}
        prelBokad={props.booking.prelBokad}
      >
        <BookingButton data-testid={props.testId} onClick={handleBookingClick}>
          <BookingContent>
            <BookingTitle>{props.booking.aktCaption}</BookingTitle>
            <BookingTime>
              {bookingTimeIntervalCaption(props.booking)}
            </BookingTime>
            <BookingLocation>{props.booking.aktLocation}</BookingLocation>
            <IconContainer>
              {props.booking.icon &&
                props.booking.icon.map(
                  (iconItem: IBookingIcon, index: number) => (
                    <EDTooltip
                      key={index}
                      placement="bottom"
                      title={iconItem.tooltip ?? ''}
                    >
                      <EDBox>
                        <EDIcon
                          key={index}
                          iconType={iconItem.icon}
                          size="small"
                          testId={iconItem.icon}
                        />
                      </EDBox>
                    </EDTooltip>
                  )
                )}
            </IconContainer>
          </BookingContent>
        </BookingButton>
      </BookingBox>
    </>
  );
};

const BookingBox = styled(EDBox, {
  name: 'BookingBox',
  shouldForwardProp: prop =>
    !['isDragging', 'prelBokad'].includes(prop as string),
})<IBookingBoxProps>(({ isDragging, prelBokad }) => ({
  position: 'absolute',
  left: 'var(--bookingLeft)',
  top: 'var(--bookingTop)',
  width: 'calc(var(--bookingWidth) - 1px)',
  height: 'var(--bookingHeight)',
  borderRadius: '5px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  zIndex: 2,
  cursor: isDragging ? 'default' : 'pointer',
  borderLeft: prelBokad ? undefined : '5px solid var(--bookingBorderColor)',
  border: prelBokad ? '2px solid var(--bookingBorderColor)' : undefined,
  background: 'var(--bookingBackground)',
  '&:hover': { background: 'var(--bookingBackgroundHover)' },
  pointerEvents: isDragging ? 'none' : 'auto',
}));

const BookingShadowBox = styled(EDBox, {
  name: 'BookingShadowBox',
  shouldForwardProp: prop => prop !== 'isDragging',
})<{
  isDragging?: boolean;
}>(({ isDragging }) => ({
  position: 'absolute',
  left: 'var(--bookingShadowLeft)',
  top: 'var(--bookingShadowTop)',
  width: 'calc(var(--bookingShadowWidth) - 1px)',
  height: 'var(--bookingShadowHeight)',
  borderRadius: '5px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  zIndex: 1,
  background: 'var(--bookingShadowBackground)',
  '&:hover': { background: 'var(--bookingBackgroundHover)' },
  pointerEvents: isDragging ? 'none' : 'auto',
}));

const BookingButton = styled(MuiButtonBase, {
  name: 'BookingButton',
})({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  padding: '3px 8px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});

const BookingContent = styled(EDBox, {
  name: 'BookingContent',
})({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const BookingTitle = styled(EDTypography, {
  name: 'BookingTitle',
})({
  fontSize: 11,
  textAlign: 'left',
  fontWeight: 'bold',
  color: 'black',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
});

const BookingTime = styled(EDTypography, {
  name: 'BookingTime',
})(({ theme }) => ({
  fontSize: 11,
  textAlign: 'left',
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
}));

const BookingLocation = styled(EDTypography, {
  name: 'BookingLocation',
})(({ theme }) => ({
  fontSize: 11,
  textAlign: 'left',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
}));

const IconContainer = styled(EDBox, {
  name: 'BookingIconContainer',
})({
  position: 'absolute',
  right: '2px',
  bottom: '2px',
  display: 'flex',
  flexDirection: 'row',
});
